import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Logo from "@/components/Logo";
import { NAV_LINKS, buildWhatsAppUrl } from "@/lib/site";
import { useSettings } from "@/context/SettingsContext";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function Header() {
  const { settings } = useSettings();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const waUrl = buildWhatsAppUrl(settings.whatsapp, "Merhaba VoyaCar Rental, araç kiralama hakkında bilgi almak istiyorum.");

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 bg-[#A11016] shadow-md" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-4">
          <Link to="/" className="shrink-0" data-testid="header-logo-link" aria-label="Ana sayfaya git">
            <Logo className="h-9 lg:h-11" />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                className={`px-2.5 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                  isActive(l.to) ? "bg-white/15 text-white" : "text-white/85 hover:bg-white/10 hover:text-white"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <a href={`tel:${settings.phone_link}`} data-testid="header-phone-link" className="flex items-center gap-2 px-2.5 py-2 text-sm font-semibold text-white whitespace-nowrap hover:bg-white/10 rounded-lg transition-colors">
              <Phone className="h-4 w-4" />
              {settings.phone}
            </a>
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="header-whatsapp-button">
              <Button className="bg-[#25D366] hover:bg-[#1EBE5D] text-white border-0 gap-2">
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp
              </Button>
            </a>
            <Link to="/araclar" data-testid="header-rent-button">
              <Button className="bg-white hover:bg-neutral-100 text-[#A11016] font-semibold">Araç Kirala</Button>
            </Link>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <a href={`tel:${settings.phone_link}`} data-testid="header-phone-mobile" className="p-2 rounded-lg text-white">
              <Phone className="h-5 w-5" />
            </a>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-trigger" className="p-2 rounded-lg text-white">
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85%] sm:w-[360px] p-0">
                <div className="flex items-center justify-between p-4 border-b">
                  <Logo className="h-9" />
                  <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="p-2"><X className="h-5 w-5" /></button>
                </div>
                <nav className="flex flex-col p-4 gap-1">
                  {NAV_LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                      className={`px-3 py-3 rounded-lg text-base font-medium ${isActive(l.to) ? "bg-[#FFF0F2] text-[#A11016]" : "text-neutral-800"}`}
                    >
                      {l.label}
                    </Link>
                  ))}
                </nav>
                <div className="p-4 mt-auto flex flex-col gap-3 border-t">
                  <a href={waUrl} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} data-testid="mobile-whatsapp-button">
                    <Button className="w-full bg-[#25D366] hover:bg-[#1EBE5D] text-white gap-2"><WhatsAppIcon className="h-4 w-4" /> WhatsApp ile Yazın</Button>
                  </a>
                  <Link to="/araclar" onClick={() => setOpen(false)} data-testid="mobile-rent-button">
                    <Button className="w-full bg-[#A11016] hover:bg-[#820D12] text-white">Araç Kirala</Button>
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
